import { Route } from '@angular/router';
import { featureFlagGuard } from 'app/core/guards/feature-flag.guard';
import { FEATURE_FLAGS } from 'app/core/services/feature-flag.service';
import { BillingAccessGuard } from 'app/features/admin/billing/guards/billing-access.guard';
import { iamPortalAdminRedirectGuard } from 'app/features/admin/me/guards/iam-portal-redirect.guard';
import { InShellForbiddenComponent } from 'app/features/error/in-shell-forbidden/in-shell-forbidden.component';
import { InShellNotFoundComponent } from 'app/features/error/in-shell-not-found/in-shell-not-found.component';
import {
    PLATFORM_STAFF_ROLES,
    ROLE_GROUPS,
    ROLES,
} from 'app/shared/constants';

/**
 * PhotoFlow product routes.
 *
 * Super-admin commerce **manage** (plan catalog, quota, license, all-org
 * subscriptions, support refunds) → nera-sa-portal only.
 *
 * Org-facing commerce on this FE:
 * - Plan catalog / subscribe CTA → public `/pricing` (Quota public plans API)
 * - Stripe checkout return → `/billing/checkout/success|cancel`
 * - Own subscription status, plan catalog and billing history → `/billing`
 *   (PFL-1864 merged the duplicate `/subscription` page into it)
 * - Own quota → `/me/quota`
 *
 * The in-app `/plans` module was removed; legacy URLs redirect below.
 */
export const adminFeatureCoreRoutes: Route[] = [
    // Kanban #63: RouteDataAuthorizationGuard's fail-closed redirect target.
    // No `data.anyRoles` here — any authenticated shell user must be able to
    // land on it (that's the whole point), and it must never itself trigger
    // another redirect. See in-shell-forbidden.component.ts.
    {
        path: 'forbidden',
        component: InShellForbiddenComponent,
    },
    // #3809: featureFlagGuard's OFF-redirect target is the bare
    // /errors/not-found (LayoutComponent 'empty') — for an authenticated
    // user that drops the shell entirely. Same precedent as /forbidden: a
    // shell-mounted route with no anyRoles that every signed-in user can
    // land on, so a flag-gated URL 404s *in* the shell instead.
    {
        path: 'errors/not-found',
        component: InShellNotFoundComponent,
    },
    // SA dashboard ('dashboard') → nera-sa-portal only (T3.2/T3.3); pure
    // SUPER_ADMIN now lands externally via RoleRedirectGuard.
    {
        path: 'albums',
        loadChildren: () => import('app/features/admin/album/album.routes'),
        // WS-B/WS-F (PFL-2564): ALBUM_WORKERS admits EDITOR (D14) alongside
        // PHOTOGRAPHER_AND_ABOVE — see roles.constants.ts's top comment for
        // why /customers just below stays on the narrower group.
        data: { anyRoles: [...ROLE_GROUPS.ALBUM_WORKERS] },
    },
    {
        // WS-I (D16, MR-I1/MR-F6, PFL-2566): Customer directory. Same guard as
        // /albums — matches be-app CustomerController's Photographer policy.
        path: 'customers',
        loadChildren: () =>
            import('app/features/admin/customer/customer.routes'),
        data: { anyRoles: [...ROLE_GROUPS.PHOTOGRAPHER_AND_ABOVE] },
    },
    {
        // WS-F (D23/D27, MR-F4): the EDITOR-only pool screen ("Việc chờ chỉnh
        // sửa"). Deliberately EDITOR only, not ALBUM_WORKERS — a Photographer/
        // admin manages the pool from inside the album (assign/open-to-pool),
        // matching be-app EditorPoolController's PhotoFlowPolicies.Editor gate.
        path: 'editor-pool',
        loadChildren: () =>
            import('app/features/editor-pool/editor-pool.routes'),
        data: { anyRoles: [ROLES.EDITOR] },
    },
    {
        // Self-service: submit studio registration + view own status.
        // No longer a sidebar menu item (moved into /me 2026-08-06) — any
        // authenticated user can reach this via the "Register a studio" CTA
        // on their profile page. Backend OrgRegistrationController already
        // only requires NeraPolicies.Authenticated (no role restriction), so
        // the FE gate is widened to match. Approve/reject remains
        // SuperAdmin-only in nera-sa-portal (PlatformAdmin on list/approve/
        // reject APIs).
        path: 'org-registration',
        loadChildren: () =>
            import(
                'app/features/admin/org-registration/org-registration.routes'
            ),
        data: { anyRoles: [...ROLE_GROUPS.ANY_AUTHENTICATED] },
    },
    {
        // T3.8 (ui-design.md §3): the tenant Members screen (list/roles/
        // invite/revoke) is its own module (`features/admin/members`), mounted
        // here under the `studio-settings/members` URL it has always used.
        // MUST stay ahead of the `studio-settings` route below — Angular
        // matches route configs in order, and `studio-settings` alone would
        // swallow the `members` segment.
        path: 'studio-settings/members',
        loadChildren: () =>
            import('app/features/admin/members/members.routes'),
        data: { anyRoles: [...ROLE_GROUPS.ALL_ADMINS] },
    },
    {
        path: 'studio-settings',
        loadChildren: () =>
            import('app/features/admin/studio-settings/studio-settings.routes'),
        data: { anyRoles: [...ROLE_GROUPS.ALL_ADMINS] },
    },
    {
        // TD.3 re-homing (page-data-spec.md §5): sa-portal `/studio-manager`
        // (team/locations/equipment) -> fe-web, own-org tenant-admin surface.
        // NOT cross-org (see StudioManagerService's header comment for the
        // authorization-model note vs. the sa-portal original, which carried an
        // X-Target-Org override). Narrower than ALL_ADMINS: be-app
        // StudioController's AdminOrSuperAdmin policy refuses ORG_OPERATOR.
        path: 'studio-manager',
        loadChildren: () =>
            import('app/features/admin/studio-manager/studio-manager.routes'),
        data: { anyRoles: [...ROLE_GROUPS.ORG_OWNER_OR_SUPER_ADMIN] },
    },
    {
        // TD.3 re-homing (page-data-spec.md §5): sa-portal `/org-structure`
        // (groups/departments) -> fe-web, own-org tenant-admin surface. Same
        // ALL_ADMINS guard as every other tenant admin page here — NOT
        // SuperAdmin-only, and NOT cross-org. Unlike Studio Manager, the
        // sa-portal original here was already free of the X-Target-Org
        // override (its own header comment says so) — verified again
        // directly against GroupsController/DepartmentsController for this
        // move; see OrgStructureService's header comment for the full
        // authorization-model note. Chatbot (`/chatbot`) is re-homed below
        // (PFL-1637) — the ChatBot permission-seed blocker previously noted
        // here is resolved: `PhotoFlow.Composition/SeedData/PhotoFlowIamSeedData.cs`
        // already grants `chatbot:*` to org Admin and `chatbot:*:read` to org
        // User roles. RBAC (roles/permissions/groups) stays on
        // nera-iam-portal `/roles` — never re-homed here (PFL-1637's fe-web
        // `/rbac` port was found to duplicate an already-active
        // nera-iam-portal admin surface hitting the same Iam.API endpoints
        // and was removed; see fallback-navigation.ts's `iam-portal`
        // sisterShell entry).
        path: 'org-structure',
        loadChildren: () =>
            import('app/features/admin/org-structure/org-structure.routes'),
        data: { anyRoles: [...ROLE_GROUPS.ALL_ADMINS] },
    },
    {
        // TD.3 leftover re-homing (PFL-1637, page-data-spec.md §5):
        // sa-portal `/chatbot` (knowledge bases) -> fe-web, platform-staff
        // manage surface. PLATFORM_STAFF_ROLES only — NOT ALL_ADMINS and NOT
        // any org tier: KB management was withdrawn from org owner/operator
        // ("org can use the chatbot, not manage it") and chatbot-service's
        // `admin` policy action enforces the same set server-side. Org roles
        // keep `read`/`write` (chat, conversations) there.
        path: 'chatbot',
        loadChildren: () =>
            import('app/features/admin/chatbot/chatbot.routes'),
        // Unleash pfl-chatbot (mvp-release-scope §5.2): off on uat.
        canActivate: [featureFlagGuard(FEATURE_FLAGS.CHATBOT)],
        data: { anyRoles: [...PLATFORM_STAFF_ROLES] },
    },
    // Admin surfaces owned by nera-iam-portal. Same ALL_ADMINS gate as the
    // sidebar's Identity/RBAC entries, so a non-admin gets the in-shell
    // /forbidden and an admin is handed to the IAM page instead of the
    // generic 404 these URLs used to fall into (kb#5782, kb#5791).
    {
        path: 'users',
        canActivate: [iamPortalAdminRedirectGuard],
        data: {
            anyRoles: [...ROLE_GROUPS.ALL_ADMINS],
            iamAdminPath: '/users',
        },
        children: [],
    },
    {
        path: 'rbac',
        canActivate: [iamPortalAdminRedirectGuard],
        data: {
            anyRoles: [...ROLE_GROUPS.ALL_ADMINS],
            iamAdminPath: '/roles',
        },
        children: [],
    },
    {
        path: 'overview',
        loadChildren: () =>
            import('app/features/admin/org-overview/org-overview.routes'),
        // PFL-1680: usage banner (classy layout) is scoped to Overview/quota/billing.
        data: {
            anyRoles: [...ROLE_GROUPS.PHOTOGRAPHER_AND_ABOVE],
            showQuotaBanner: true,
        },
    },
    {
        path: 'me',
        loadChildren: () => import('app/features/admin/me/me.routes'),
    },
    // Legacy /plans → public pricing catalog
    { path: 'plans', pathMatch: 'full', redirectTo: '/pricing' },
    {
        path: 'plans/billing/success',
        redirectTo: '/billing/checkout/success',
    },
    {
        path: 'plans/billing/cancel',
        redirectTo: '/billing/checkout/cancel',
    },
    {
        path: 'billing',
        loadChildren: () => import('app/features/admin/billing/billing.routes'),
        // #61 security fix: role-only `anyRoles: PHOTOGRAPHER_AND_ABOVE` let a STUDIO
        // photographer reach the studio's own billing data too — it was widened only so a
        // SOLO SystemOrg photographer's Stripe checkout return
        // (/billing/checkout/{success,cancel}) never 403s. BillingAccessGuard keeps that
        // SystemOrg case working while restricting a studio to ORG_OWNER/platform staff
        // (WS-B §7.2), matching the be-app BillingSubjectAccessGuard rule.
        // Unleash pfl-billing (mvp-release-scope §5.2) first: off on uat, so the
        // URL is not-found before any access rule is evaluated.
        canActivate: [
            featureFlagGuard(FEATURE_FLAGS.BILLING),
            BillingAccessGuard,
        ],
        // PFL-1680: usage banner (classy layout) is scoped to Overview/quota/billing.
        data: {
            showQuotaBanner: true,
        },
    },
];
